// src/pages/AboutUs.tsx
import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import Button from "../shared/button/Button";
import Title from "../components/typography/Title";
import Description from "../components/typography/Description";
import {
  Lightbulb,
  Eye,
  Heart,
  Trophy,
  UsersThree,
  LinkedinLogo,
} from "@phosphor-icons/react";
import { client } from "../sanity/client";
import { urlFor } from "../sanity/image";
import { TEAM_MEMBERS_QUERY, ABOUT_PAGE_QUERY } from "../sanity/queries";

// ─── Sanity Team Member type ────────────────────────────────────────────────
interface SanityTeamMember {
  _id: string;
  name: string;
  role: string;
  bio?: string;
  linkedinUrl?: string;
  photo?: {
    asset: { _id: string; url: string };
    hotspot?: { x: number; y: number };
  };
}

// ─── About page data from Sanity ─────────────────────────────────────────────
interface AboutPageData {
  heroImage?: {
    asset: { _id: string; url: string };
    hotspot?: { x: number; y: number };
  };
}

// ─── Accent palette (same CSS-var system as Product/Solution pages) ─────────
const accentPalette = {
  pageBg: "#071C22",
  panelBg: "#0A2630",
  acc: "#20B8CD",
  accA: "#20B8CD",
  accB: "#1B7683",
  glow: "rgba(32,184,205,0.35)",
  border: "rgba(32,184,205,0.22)",
};

const cssVars: React.CSSProperties = {
  ["--pageBg" as string]: accentPalette.pageBg,
  ["--panelBg" as string]: accentPalette.panelBg,
  ["--acc" as string]: accentPalette.acc,
  ["--accA" as string]: accentPalette.accA,
  ["--accB" as string]: accentPalette.accB,
  ["--accGlow" as string]: accentPalette.glow,
  ["--accBorder" as string]: accentPalette.border,
} as React.CSSProperties;

// ─── Gradient fallbacks for avatars ──────────────────────────────────────────
const avatarGradients = [
  "from-teal-500 to-cyan-500",
  "from-cyan-500 to-blue-500",
  "from-blue-500 to-violet-500",
  "from-violet-500 to-purple-500",
  "from-emerald-500 to-teal-500",
  "from-amber-500 to-orange-500",
];

function getInitials(name: string): string {
  return name
    .split(" ")
    .map((w) => w[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);
}

// ─── Team Card — circular avatar with info below ─────────────────────────────
const TeamCard: React.FC<{ member: SanityTeamMember; index: number }> = ({
  member,
  index,
}) => {
  const gradient = avatarGradients[index % avatarGradients.length];
  const initials = getInitials(member.name);
  const photoUrl = member.photo?.asset
    ? urlFor(member.photo).width(200).height(200).fit("crop").url()
    : null;

  return (
    <div className="group flex flex-col items-center rounded-2xl border border-white/[0.06] bg-white/[0.03] px-6 py-8 text-center transition-all hover:border-[var(--accBorder)] hover:bg-white/[0.05]">
      {/* Avatar */}
      {photoUrl ? (
        <img
          src={photoUrl}
          alt={member.name}
          className="group-hover:ring-[var(--acc)]/50 h-24 w-24 rounded-full object-cover ring-2 ring-white/10 transition-all duration-300"
        />
      ) : (
        <div
          className={`flex h-24 w-24 items-center justify-center rounded-full bg-gradient-to-br ${gradient} group-hover:ring-[var(--acc)]/50 text-2xl font-bold text-white ring-2 ring-white/10 transition-all duration-300`}
        >
          {initials}
        </div>
      )}

      {/* Info */}
      <h3 className="mt-4 text-base font-medium text-white">{member.name}</h3>
      <p className="mt-1 text-sm text-[var(--acc)]">{member.role}</p>
      {member.bio && (
        <p className="mt-3 text-sm leading-relaxed text-gray-400">
          {member.bio}
        </p>
      )}

      {/* LinkedIn */}
      {member.linkedinUrl && (
        <a
          href={member.linkedinUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="mt-4 inline-flex items-center gap-1.5 text-sm text-gray-400 transition-colors hover:text-[var(--acc)]"
          aria-label={`${member.name} LinkedIn`}
        >
          <LinkedinLogo weight="bold" className="h-4 w-4" />
          LinkedIn
        </a>
      )}
    </div>
  );
};

// ─── Main Component ──────────────────────────────────────────────────────────
const AboutUs: React.FC = () => {
  const [teamMembers, setTeamMembers] = useState<SanityTeamMember[]>([]);
  const [teamLoading, setTeamLoading] = useState(true);
  const [aboutData, setAboutData] = useState<AboutPageData | null>(null);

  useEffect(() => {
    client
      .fetch<SanityTeamMember[]>(TEAM_MEMBERS_QUERY)
      .then((data) => {
        setTeamMembers(data ?? []);
        setTeamLoading(false);
      })
      .catch((err) => {
        console.error("Error fetching team members:", err);
        setTeamLoading(false);
      });

    client
      .fetch<AboutPageData>(ABOUT_PAGE_QUERY)
      .then((data) => setAboutData(data))
      .catch((err) => console.error("Error fetching about page:", err));
  }, []);

  const heroImageUrl = aboutData?.heroImage?.asset
    ? urlFor(aboutData.heroImage).width(800).fit("max").url()
    : null;

  return (
    <>
      <Helmet>
        <title>About Us | Babblebots - AI-Powered Recruitment Platform</title>
        <meta
          name="description"
          content="Learn about Babblebots' mission to transform recruitment with AI. Discover our story, values, and commitment to making hiring faster, fairer, and more effective."
        />
      </Helmet>

      <div
        style={cssVars}
        className="min-h-screen bg-[var(--pageBg)] text-white"
      >
        {/* ════════════ HERO with stats ════════════ */}
        <section className="relative overflow-hidden px-4 pb-16 pt-20 md:px-12 md:pb-24 md:pt-32 lg:pt-40">
          {/* Ambient glow */}
          <div
            className="absolute inset-0"
            style={{
              backgroundImage: `
                radial-gradient(1100px 600px at 0% 20%, var(--accGlow), transparent 55%),
                radial-gradient(600px 400px at 90% 80%, rgba(255,255,255,0.04), transparent 60%)
              `,
            }}
          />
          {/* Ghost word */}
          {/* <div className="pointer-events-none absolute -right-8 top-1/2 -translate-y-1/2 select-none font-serif text-[12rem] font-thin leading-none tracking-tight text-white/[0.02] md:text-[18rem] lg:text-[22rem]">
            About
          </div> */}

          <div className="relative mx-auto max-w-6xl">
            <div className="flex flex-col items-center gap-12 lg:flex-row lg:gap-16">
              {/* Left — text */}
              <div className="flex-1">
                <p className="bg-[var(--acc)]/10 mb-6 inline-block rounded-full border border-[var(--accBorder)] px-4 py-1.5 font-sans text-xs font-medium uppercase tracking-[3px] text-[var(--acc)]">
                  About Us
                </p>
                <Title as="h1" size="xl" className="!text-left leading-tight">
                  Building the Future
                  <br />
                  <span className="text-[var(--acc)]">of Recruitment</span>
                </Title>
                <p className="mt-6 max-w-2xl text-lg font-light leading-relaxed text-gray-300">
                  We're on a mission to make hiring faster, fairer, and more
                  effective. By combining AI intelligence with human expertise,
                  we're transforming how organizations discover and hire talent.
                </p>
              </div>

              {/* Right — hero image from Sanity */}
              {heroImageUrl && (
                <div className="w-full flex-shrink-0 lg:w-[45%]">
                  <div className="relative overflow-hidden rounded-2xl border border-white/[0.06]">
                    <img
                      src={heroImageUrl}
                      alt="About Babblebots"
                      className="h-auto w-full object-contain"
                    />
                    {/* Subtle glow behind image */}
                    <div className="absolute -bottom-8 -right-8 h-32 w-32 rounded-full bg-[var(--accGlow)] blur-3xl" />
                  </div>
                </div>
              )}
            </div>

            {/* Stats strip */}
            <div className="mt-16 flex flex-col items-stretch divide-y divide-white/10 rounded-2xl border border-white/10 bg-white/[0.03] backdrop-blur-md sm:flex-row sm:divide-x sm:divide-y-0">
              {[
                { value: "200+", label: "Companies Served" },
                { value: "20K+", label: "AI Interviews" },
                { value: "85%", label: "Faster Hiring" },
                { value: "10+", label: "Countries" },
              ].map((stat, i) => (
                <div
                  key={i}
                  className="flex flex-1 flex-col items-center justify-center px-6 py-8"
                >
                  <span className="font-serif text-4xl font-light text-[var(--acc)] md:text-5xl">
                    {stat.value}
                  </span>
                  <span className="mt-2 text-xs font-medium uppercase tracking-widest text-gray-400">
                    {stat.label}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ════════════ TEAM — magazine-style photo cards ════════════ */}
        {!teamLoading && teamMembers.length > 0 && (
          <section className="px-4 py-12 md:px-12">
            <div className="mx-auto max-w-6xl">
              <Title size="lg" className="mb-4">
                Team Babblebots
              </Title>
              <Description className="mx-auto mb-14 max-w-2xl">
                Reimagining the hiring landscape, one algorithm at a time.
              </Description>

              <div className="grid gap-5 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
                {teamMembers.map((member, i) => (
                  <TeamCard key={member._id} member={member} index={i} />
                ))}
              </div>
            </div>
          </section>
        )}
      </div>
    </>
  );
};

export default AboutUs;
